<template>
  <v-btn
    @click="handleClick"
    :append-icon="appendIcon && !iconOnly ? appendIcon : undefined"
    :aria-label="useAriaLabel"
    :block="fullWidth"
    :class="customClasses"
    :color="color"
    :data-analytics-event="analyticsEvent"
    :data-prevent-double-click="preventDoubleClick"
    :data-testid="dataTestId"
    :icon="iconOnly"
    :loading="loading"
    :min-height="minHeight"
    :min-width="minWidth"
    :prepend-icon="prependIcon && !iconOnly ? prependIcon : undefined"
    :size="size"
    :variant="variant"
  >
    <span v-if="!iconOnly">{{ textTranslate ? $bct(text) : text }}</span>

    <v-icon
      v-if="iconOnly && icon"
      :icon="icon"
      :size="iconSize"
    />
  </v-btn>
</template>

<script setup>
import _ from 'lodash';

const $bct = inject('translator');

const emit = defineEmits(['click']);

const props = defineProps({
  // Text and icon placement

  appendIcon: {
    type: String,
    default: '',
  },

  ariaLabel: {
    type: String,
    default: '',
  },

  text: {
    type: String,
    required: true,
  },

  textTranslate: {
    type: Boolean,
    default: true,
  },

  icon: {
    type: String,
    default: '',
  },

  iconOnly: {
    type: Boolean,
    default: false,
  },

  // default in Vuetify translates to 24px
  iconSize: {
    type: [String, Number],
    default: 'default',
  },

  prependIcon: {
    type: String,
    default: '',
  },

  preventDoubleClick: {
    type: Boolean,
    default: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },

  // data and test IDs - might remove analytics but including for now
  analyticsEvent: {
    type: String,
    default: '',
  },

  dataTestId: {
    type: String,
    default: null,
  },

  // Other styling
  color: {
    type: String,
    default: 'primary',
  },

  customClasses: {
    type: String,
    default: '',
  },

  fullWidth: {
    type: Boolean,
    default: false,
  },

  size: {
    type: String,
    default: 'default',
  },

  variant: {
    type: String,
    default: 'elevated',
  },
});

// WCAG 2.1 AAA compliance for mobile target size
const minWidth = '3rem';
const minHeight = '3rem';

const useAriaLabel = computed(() => {
  // This would be a rare edge case but output to browser that we need to fix this scenario
  if (props.iconOnly && !props.ariaLabel) {
    console.warn('Icon-only buttons require an aria-label for accessibility compliance');
  }

  if (props.ariaLabel) {
    return $bct(props.ariaLabel);
  }
  // Otherwise, return text. We don't need aria-label if we have text.
  return props.textTranslate ? $bct(props.text) : props.text;
});

// Use lodash debounce to prevent double clicks
// https://lodash.com/docs/4.17.15#debounce
const debouncedEmit = _.debounce((event) => emit('click', event), 1000, {
  leading: true,
  trailing: false,
});

function handleClick(event) {
  // When loading, we do not emit click
  if (props.loading) return;

  if (props.preventDoubleClick) {
    debouncedEmit(event);
  } else {
    emit('click', event);
  }
}
</script>

<style scoped>
/* Ensure minimum touch target size for WCAG compliance; is mobile enhancement
48px = 3 rem */
.v-btn {
  min-width: 3rem !important;
  min-height: 3rem !important;
}

/* Responsive sizing for different screen sizes */
@media (min-width: 48rem) {
  /* 768px */
  .v-btn {
    min-width: 2.75rem !important;
    min-height: 2.75rem !important;
  }
}

/* This adds invisible pseudo-element for larger touch target */
.v-btn::before {
  content: '';
  position: absolute;
  top: -0.5rem;
  left: -0.5rem;
  right: -0.5rem;
  bottom: -0.5rem;
}

/* This is an OS option for those with motion sensitivity; only impacts if they select this option */
@media (prefers-reduced-motion: reduce) {
  .v-btn {
    transition: none !important;
  }
}
</style>
